import {Litepicker} from "litepicker/dist/nocss/litepicker.umd";
import "litepicker/dist/nocss/plugins/mobilefriendly";
import "litepicker/dist/plugins/ranges";
import 'litepicker/dist/plugins/multiselect';
import SelectBox from "../../Atom/SelectBox/SelectBox";

export default class Scheduler {
    labels = {}

    static init() {
        Array.prototype.forEach.call(document.querySelectorAll('.scheduler'), el => {
            new Scheduler(el);
        });
    }

    constructor(element) {
        if (!element) return;
        this.element = element;
        this.calendarElement = this.element.querySelector('.scheduler__calendar');
        this.list = this.element.querySelector('.scheduler__list');
        this.form = this.element.querySelector('.scheduler__list__form');
        this.language = document.querySelector('html').getAttribute('lang') || 'en-US';
        this.list.id = 'paginated-list';
        this.list.dataset.currentPage = '1';
        this.list.setAttribute('aria-live', "polite");
        this.labels.name = this.form.dataset && this.form.dataset.nameSelectPlaceholder ? this.form.dataset.nameSelectPlaceholder : 'Name';
        this.labels.upcoming = this.form.dataset && this.form.dataset.upcomingEvents ? this.form.dataset.upcomingEvents : 'Upcoming';
        this.labels.ytd = this.form.dataset && this.form.dataset.ytdEvents ? this.form.dataset.ytdEvents : 'YTD';
        this.labels.previousPage = this.form.dataset && this.form.dataset.previousPage ? this.form.dataset.previousPage : 'Previous page';
        this.labels.nextPage = this.form.dataset && this.form.dataset.nextPage ? this.form.dataset.nextPage : 'Next page';
        this.labels.page = this.form.dataset && this.form.dataset.page ? this.form.dataset.page : 'Page';
        this.labels.scrollLeft = this.form.dataset && this.form.dataset.scrollLeft ? this.form.dataset.scrollLeft : 'Scroll left';
        this.labels.scrollRight = this.form.dataset && this.form.dataset.scrollRight ? this.form.dataset.scrollRight : 'Scroll right';
        this.labels.loadingFailed = this.form.dataset && this.form.dataset.loadingFailed ? this.form.dataset.loadingFailed : 'Loading failed';

        (async () => {
            let entries = await fetch(this.element.dataset.entriesUri)
                .then(response => response.json())
                .catch(error => {
                    console.error(error);
                    alert(this.labels.loadingFailed);
                });

            const uniqueNames = [...new Set(entries.map(item => item.title))];
            const uniqueYears = [...new Set(entries.map(item => (new Date(parseInt(item.date))).getFullYear()))];
            const uniqueDates = [...new Set(entries.map(item => item.date))];
            const largestYear = Math.max(...uniqueYears);
            const smallestYear = Math.min(...uniqueYears);
            const smallestDate = Math.min(...uniqueDates);
            const now = new Date();
            const nameFilterOptions = uniqueNames.map((name) => {
                return {
                    label: name,
                    value: name
                }
            });
            const dateFilterOptions = [
                {
                    label: this.labels.upcoming,
                    value: now.getTime() + '@ASC',
                    selected: true
                },
                {
                    label: this.labels.ytd,
                    value: (new Date(new Date().getFullYear(), 0, 1).getTime()) + '#' + (now - 1) + '@DESC'
                },
                ...uniqueYears.filter(year => year < now.getFullYear()).sort((aYear, bYear) => bYear - aYear).map((year) => {
                    return {
                        label: year,
                        value: (new Date(year, 0, 1).getTime()) + '#' + (new Date(year + 1, 0, 1).getTime() - 1) + '@DESC'
                    }
                })
            ];
            const nameSelectTarget = this.form.querySelector('.scheduler__list__form-type');
            let nameSelect = new CustomSelect('event_name', this.labels.name, nameFilterOptions, nameSelectTarget);
            const yearSelectTarget = this.element.querySelector('.scheduler__list__form-year');
            let yearSelect = new CustomSelect('event_year', null, dateFilterOptions, yearSelectTarget);
            const filter = () => {
                let filteredEntries = entries;

                // by name
                let name = nameSelect.selectBox.value;
                filteredEntries = name ? filteredEntries.filter(el => el.title === name) : filteredEntries;

                // by date
                const [filterDateRange, sortDirection] = yearSelect.selectBox.value.split('@');
                const [filterDateFrom, filterDateTo] = filterDateRange.split('#');
                filteredEntries = filteredEntries.filter(item => item.date >= filterDateFrom && (!filterDateTo || item.date <= filterDateTo)).sort((a, b) => sortDirection === 'ASC' ? a.date - b.date : b.date - a.date);

                return filteredEntries;
            }

            window.addEventListener('click', (e) => {
                // select options & date filter
                if (e.target && e.target.classList.contains('select-box__option')) {
                    if (!!nameSelect.selectBox.value) {
                        this.form.classList.add('is-dirty');
                    }
                    this.renderList(filter());
                }

                if (e.target && e.target.id.indexOf('past-future-switch') >= 0) {
                    this.renderList(filter());
                }

                // clear
                if (e.target && e.target.classList.contains('scheduler__list__form__clear')) {
                    nameSelect.selectBox.clear();
                    this.renderList(filter());
                    this.form.classList.remove('is-dirty');
                }

                // close mobile popup view
                if (e.target && e.target.classList.contains('scheduler__mobile__popup')) {
                    this.calendar.clearSelection();
                }
            });

            // create list view
            this.renderList(filter());

            // create calendar view
            let scrollButtons = document.createElement('DIV');
            scrollButtons.classList.add('container__scroll-buttons');
            scrollButtons.innerHTML = `
                <button class="container__scroll-button container__scroll-button--left" id="scroll-left" aria-label="${this.labels.scrollLeft}" title="${this.labels.scrollLeft}">
                    ${this.labels.scrollLeft}
                </button>
                <button class="container__scroll-button container__scroll-button--right container__scroll-button--is-available" id="scroll-right" aria-label="${this.labels.scrollRight}" title="${this.labels.scrollRight}">
                    ${this.labels.scrollRight}
                </button>`
            let scrollButtonsArray = Array.from(scrollButtons.querySelectorAll('button'));

            scrollButtonsArray.forEach(element => {
                element.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();

                    let month = this.element.querySelector('.month-item');

                    const type = element.classList.contains('container__scroll-button--left') ? 'left' : 'right';

                    month.scrollTo({top: 0, left: month.scrollLeft + month.offsetWidth * (type === 'left' ? -1 : 1), behavior: 'smooth'});
                });
            });

            this.calendar = new Litepicker({
                element: this.calendarElement,
                singleMode: true,
                autoRefresh: false,
                dropdowns: {"minYear": smallestYear, "maxYear": largestYear, "months": true, "years": true},
                minDate: new Date(smallestDate ? parseInt(smallestDate) : new Date('1 Jan 2025')),
                inlineMode: true,
                lang: this.language,
                autoApply: true,
                tooltipText: {
                    one: element.dataset.dayLabel,
                    other: element.dataset.daysLabel,
                },
                buttonText: {
                    previousMonth: '<span class="litepicker__button litepicker__button--prev"></span>',
                    nextMonth: '<span class="litepicker__button litepicker__button--next"></span>'
                },
                plugins: [],
                ranges: false,
                tooltipNumber: (totalDays) => {
                    return totalDays - 1;
                },
                lockDaysFilter: day => false,
                setup: (picker) => {
                    picker.on('render:month', (month, date) => {
                        this.monthContainer = month;
                        const days = month.querySelector('.container__days');
                        const pre = days.querySelectorAll(':not([class])') || [];
                        const daysInMonth = days.children.length - pre.length;
                        const calendars = picker.calendars;
                        const isFirst = calendars[0].isSame(date, 'day');
                        const isLast = calendars[calendars.length - 1].isSame(date, 'day');

                        if (isFirst) {
                            const before = date.clone();
                            pre.forEach((element) => {
                                days.removeChild(element);
                                before.subtract(1, 'day');
                                const day = picker.renderDay(before);
                                day.classList.add('is-pre');
                                days.prepend(day);
                            });
                        }

                        if (isLast) {
                            const after = date.clone().add(daysInMonth, 'days');
                            const maxDays = days.children.length > 35 ? 42 : 35;
                            while (days.children.length < maxDays) {
                                const day = picker.renderDay(after);
                                day.classList.add('is-post');
                                days.appendChild(day);
                                after.add(1, 'day');
                            }
                        }

                        // add scroll buttons
                        if (window.innerWidth >= 768) return;
                        if (!picker.options.element.classList.contains('scheduler__calendar--has-mobile-buttons')) return;
                        if (picker.ui.parentElement.querySelector('.container__scroll-buttons')) return;
                        month.classList.add('scroll-buttons--is-rendered');
                        picker.ui.after(scrollButtons);
                    });

                    picker.on('render:day', (day, date) => {
                        entries.forEach(el => {
                            let calendarDate = date.dateInstance;
                            let appointmentDate = new Date(parseInt(el.date));

                            if (!this.isSameDay(appointmentDate, calendarDate)) return;

                            let weekDay = calendarDate.getDay();
                            let positionStyle = (weekDay > 3 || weekDay === 0 ? 'right: 100%; transform: translate(50px, 0);' : 'left: 100%; transform: translate(-50px, 0);');

                            if (day.querySelectorAll('.scheduler__badge').length === 0) {
                                let badgeContainerElement = document.createElement('DIV');
                                badgeContainerElement.className = `scheduler__badge`
                                day.append(badgeContainerElement);
                            }

                            let badgeContainerElement = day.querySelector('.scheduler__badge')
                            let badgeItemElement = document.createElement('DIV');
                            badgeItemElement.style.setProperty("--color", `${el.color}`);
                            badgeItemElement.className = `scheduler__badge__item`;
                            badgeItemElement.innerHTML = `<a href="${el.url}" class="scheduler__badge__item__name">${el.shortTitle}</a>
                            <a href="${el.url}" class="scheduler__badge__item__content" style="${positionStyle}">
                                <p class="scheduler__badge__item__content__title h4 mb-0">${el.title}</p>
                                <p class="scheduler__badge__item__content__time">${appointmentDate.toLocaleTimeString([], {timeStyle: 'short'})}</p>
                                <p class="scheduler__badge__item__content__text">${el.location}</p>
                                <p class="scheduler__badge__item__content__text">${el.publicLabel}</p>
                            </a>`
                            badgeContainerElement.append(badgeItemElement);
                        });
                    });

                    picker.on('selected', (calendarDate) => {
                        entries.forEach(el => {
                            let appointmentDate = new Date(parseInt(el.date));

                            if (!this.isSameDay(appointmentDate, calendarDate.dateInstance)) return;

                            let weekDay = calendarDate.getDay();
                            let positionStyle = (weekDay > 3 || weekDay === 0 ? 'right: 100%; transform: translate(50px, 0);' : 'left: 100%; transform: translate(-50px, 0);');

                            if (document.body.querySelectorAll('.scheduler__mobile__popup').length === 0) {
                                let badgeContainerElement = document.createElement('DIV');
                                badgeContainerElement.className = `scheduler__mobile__popup`
                                document.body.append(badgeContainerElement);
                            }

                            let badgeContainerElement = document.querySelector('.scheduler__mobile__popup');

                            let badgeItemElement = document.createElement('DIV');
                            badgeItemElement.style.setProperty("--color", `${el.color}`);
                            badgeItemElement.className = `scheduler__badge__item`;
                            badgeItemElement.innerHTML = `<a href="${el.url}" class="scheduler__badge__item__name">${el.shortTitle}</a>
                            <a href="${el.url}" class="scheduler__badge__item__content" style="${positionStyle}">
                                <p class="scheduler__badge__item__content__title h4 mb-0">${el.title}</p>
                                <p class="scheduler__badge__item__content__time">${appointmentDate.toLocaleTimeString([], {timeStyle: 'short'})}</p>
                                <p class="scheduler__badge__item__content__text">${el.location}</p>
                                <p class="scheduler__badge__item__content__text">${el.publicLabel}</p>
                            </a>`
                            badgeContainerElement.append(badgeItemElement);
                        });
                    });

                    picker.on('clear:selection', () => {
                        let badgeContainerElement = document.querySelector('.scheduler__mobile__popup');
                        if (badgeContainerElement) {
                            badgeContainerElement.remove();
                        }
                    });
                }
            });

            this.calendar.hide = () => {}; // hide method override!

            window.addEventListener('scroll', (e) => {
                if (!e.target.classList || !e.target.classList.contains('month-item')) return;
                let month = e.target;

                Array.from(scrollButtons.querySelectorAll('button')).forEach(element => {
                    const type = element.classList.contains('container__scroll-button--left') ? 'left' : 'right';
                    let containerRect = month.getBoundingClientRect();
                    let isEdgeLeft = type === 'left' && month.scrollLeft <= 10;
                    let isEdgeRight = type === 'right' && month.scrollWidth - month.scrollLeft - containerRect.width <= 10

                    element.classList[isEdgeLeft || isEdgeRight ? 'remove' : 'add']('container__scroll-button--is-available');
                });
            }, true);
        })();
    }

    renderList(entries) {
        this.list.innerHTML = this.form.dataset && this.form.dataset.nothing ? this.form.dataset.nothing : 'Nothing found';
        if (!entries || entries.length === 0) return;

        this.list.innerHTML = '';
        let paginationContainer = document.createElement('NAV');
        paginationContainer.classList.add('pagination-container');
        paginationContainer.innerHTML = `
            <button class="pagination-button" id="prev-button" aria-label="${this.labels.previousPage}" title="${this.labels.previousPage}">
                ${this.labels.previousPage}
            </button>
            <div id="pagination-numbers"></div>
            <button class="pagination-button" id="next-button" aria-label="${this.labels.nextPage}" title="${this.labels.nextPage}">
                ${this.labels.nextPage}
            </button>`
        this.list.append(paginationContainer);

        entries.forEach(el => {
            let appointmentDate = new Date(parseInt(el.date));
            let badgeItemElement = document.createElement('DIV');
            badgeItemElement.style.setProperty("--color", `${el.color}`);
            badgeItemElement.title = `${el.title}`;
            badgeItemElement.className = `scheduler__list__item`;
            badgeItemElement.innerHTML = `
                    <a href="${el.url}" class="scheduler__list__item__content">
                        <p class="scheduler__list__item__content__title h4 mb-0">${el.title}</p>
                        <p class="scheduler__list__item__content__text">
                            ${appointmentDate.toLocaleDateString(undefined, {day: '2-digit', month: '2-digit', year: 'numeric'})}
                            &nbsp;|&nbsp;
                            ${appointmentDate.toLocaleTimeString(undefined, {timeStyle: 'short'})}
                            &nbsp;|&nbsp;
                            ${el.location}
                            &nbsp;|&nbsp;
                            ${el.publicLabel}
                        </p>
                    </a>`
            this.list.append(badgeItemElement);
        });

        new Pagination(this.element.querySelector('.pagination-container'), parseInt(this.element.dataset.pageLimit), this.labels.page);
    }

    getData(url) {
        return fetch(url)
            .then(response => response.json())
            .catch((error) => {
                console.log(error);
            });
    }

    isSameDay(d1, d2) {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();
    }
}


class Pagination {
    pageLabel = 'Page';

    constructor(elementNode, pageLimit, pageLabel) {
        if (!elementNode) return;

        this.elementNode = elementNode;
        this.paginationNumbers = this.elementNode.querySelector('#pagination-numbers');
        this.paginatedList = document.querySelector('#paginated-list');
        this.listItems = this.paginatedList.querySelectorAll('.scheduler__list__item');
        this.nextButton = this.elementNode.querySelector('#next-button');
        this.prevButton = this.elementNode.querySelector('#prev-button');
        this.paginationLimit = pageLimit || 10;
        this.pageCount = Math.ceil(this.listItems.length / this.paginationLimit);
        this.pageLabel = pageLabel || 'Page';

        // If less than pagination limit, hide pagination
        if (this.pageCount <= 1) {
            this.elementNode.style.display = 'none';
            return;
        }

        this.currentPage = 1;

        this.getPaginationNumbers();
        this.setCurrentPage(1);

        this.prevButton.addEventListener('click', () => {
            this.setCurrentPage(this.currentPage - 1);
        });

        this.nextButton.addEventListener('click', () => {
            this.setCurrentPage(this.currentPage + 1);
        });

        document.querySelectorAll('.pagination-number').forEach((button) => {
            const pageIndex = Number(button.getAttribute('page-index'));

            if (pageIndex) {
                button.addEventListener('click', () => {
                    this.setCurrentPage(pageIndex);
                });
            }
        });
    }

    disableButton(button) {
        button.classList.add('disabled');
        button.setAttribute('disabled', true);
    }

    enableButton(button) {
        button.classList.remove('disabled');
        button.removeAttribute('disabled');
    }

    handlePageButtonsStatus() {
        if (this.currentPage === 1) {
            this.disableButton(this.prevButton);
        } else {
            this.enableButton(this.prevButton);
        }

        if (this.pageCount === this.currentPage) {
            this.disableButton(this.nextButton);
        } else {
            this.enableButton(this.nextButton);
        }
    }

    handleActivePageNumber() {
        document.querySelectorAll('.pagination-number').forEach((button) => {
            button.classList.remove('active');
            const pageIndex = Number(button.getAttribute('page-index'));
            if (pageIndex === this.currentPage) {
                button.classList.add('active');
            }
        });
    }

    appendPageNumber(index) {
        const pageNumber = document.createElement('button');
        pageNumber.className = 'pagination-number';
        pageNumber.innerHTML = index;
        pageNumber.setAttribute('page-index', index);
        pageNumber.setAttribute('aria-label', `${this.pageLabel} ${index}`);

        this.paginationNumbers.append(pageNumber);
    }

    getPaginationNumbers() {
        for (let i = 1; i <= this.pageCount; i++) {
            this.appendPageNumber(i);
        }
    }

    setCurrentPage = (pageNum) => {
        this.currentPage = pageNum;

        this.handleActivePageNumber();
        this.handlePageButtonsStatus();

        const prevRange = (pageNum - 1) * this.paginationLimit;
        const currRange = pageNum * this.paginationLimit;

        this.listItems.forEach((item, index) => {
            item.classList.add('hidden');
            if (index >= prevRange && index < currRange) {
                item.classList.remove('hidden');
            }
        });
    };
}


class CustomSelect {
    constructor(id, placeholder, elementsArray, targetElement) {
        if (!id || !elementsArray || !targetElement) return;
        this.elementsArray = elementsArray;
        this.targetElement = targetElement;
        this.render(id, placeholder, elementsArray);
    }

    render(id, placeholder, elements) {
        let optionHTML = (label, value) => {
            let option = document.createElement('DIV');
            option.classList.add('select-box__option');
            option.innerHTML = `
                <input type="radio" class="radio" value="${value}" id="select-box--${id}" name="event_title">
                <label class="select-box__option-label" for="select-box-${id}">
                    <div class="select-box__option-text">
                        <p class="select-box__option-title">${label}</p>
                    </div>
                </label>`;

            return option.outerHTML;
        }

        let options = '';
        elements.map(item => options += optionHTML(item.label, item.value));

        let element = document.createElement('DIV');
        element.setAttribute('id', id);
        element.classList.add('select-box', 'scheduler__list__form__name');
        element.setAttribute('tabindex', '0');
        const selectedValue = elements.find(item => !!item?.selected)?.value;
        if (selectedValue) {
            element.dataset.value = selectedValue;
        }
        element.innerHTML = `
            <div class="select-box__options-list">
                ${options}
            </div>

            <div class="select-box__option select-box__option--is-selected">
                <div class="select-box__option-label">
                    <div class="select-box__option-text">
                        <p class="select-box__option-title">${placeholder}</p>
                    </div>
                </div>
            </div>
        `;

        this.targetElement.append(element);
        this.selectBox = new SelectBox(element);
    }
}
